import { Card, CardContent } from "../../../../ui/card";
import { Users, Clock, AlertCircle, CreditCard, Calendar } from "lucide-react";
import { getTimeRemaining } from "../../../../../lib/utils";
import { CancelDialog } from "./CancelDialog";
import { ChatSection } from "./ChatSection";
import { QueryDialog } from "./QueryDialog";
import { useSelector } from "react-redux";
import { ServiceCompletedDialog } from "./ServiceCompletedDialog";
import { RiProgress3Line } from "react-icons/ri";
import { Button } from "../../../../ui/button";
import { IoIosRefresh } from "react-icons/io";
import { ServiceFinishedViewByHelper } from "./ServiceFinishedHelperView";
import { ServiceFinishedViewByOwner } from "./ServiceFinishedHouseOwnerView";

const renderContent = (user, job) => {
  const { jobStatus, paymentInfo, scheduledDate, scheduledTime, ShovelerInfo } = job;

  if (user.role === 'shoveller' && ['in-progress', 'completed'].includes(jobStatus) && ShovelerInfo[0]?.shovellerAction === "completed") {
    return <ServiceFinishedViewByHelper job={job} />;
  }

  if (user.role === 'houseOwner' && ['in-progress', 'completed'].includes(jobStatus) && ShovelerInfo[0]?.houseOwnerAction === "completed") {
    return <ServiceFinishedViewByOwner job={job} />;
  }

  if (user.role === 'houseOwner' && jobStatus === 'in-progress' && ShovelerInfo[0]?.shovellerAction === "completed") {
    return (
      <div className="flex flex-col items-center text-center space-y-6 py-6">
        <div className="flex flex-col items-center space-y-4">
          <RiProgress3Line className="w-12 h-12 text-gray-900" />
          <div className="space-y-2">
            <h3 className="font-bold text-xl text-gray-900">Service Completed!</h3>
            <p className="text-gray-600 text-base leading-relaxed max-w-md">
              Helper marked job as Completed. Now it's your turn!
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <QueryDialog job={job} />
          <ServiceCompletedDialog job={job} role={user.role} />
        </div>
      </div>
    );
  }

  if (user.role === 'shoveller' && jobStatus === 'in-progress') {
    return (
      <div className="flex flex-col items-center text-center space-y-6 py-6">
        <div className="flex flex-col items-center space-y-4">
          <RiProgress3Line className="w-12 h-12 text-gray-900" />
          <div className="space-y-2">
            <h3 className="font-bold text-xl text-gray-900">Service in Progress</h3>
            <p className="text-gray-600 text-base leading-relaxed max-w-md">
              You are currently providing services for this job. Communicate with the homeowner or manage the service completion.
            </p>
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <CancelDialog job={job} />
          <ChatSection job={job} label="Chat with homeowner" />
          <QueryDialog job={job} />
          <ServiceCompletedDialog job={job} role={user.role} />
        </div>
      </div>
    );
  }

  if (jobStatus === 'not-anymore' && paymentInfo.status === 'canceled') {
    return (
      <div className="flex flex-col items-center text-center space-y-6 py-6">
        <div className="flex flex-col items-center space-y-4">
          <AlertCircle className="w-12 h-12 text-gray-900" />
          <div className="space-y-2">
            <h3 className="font-bold text-xl text-gray-900">Job Cancelled</h3>
            <p className="text-gray-600 text-base leading-relaxed max-w-md">
              This job has been cancelled. The payment authorization has been released and no charges were made.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // In Progress
  if (user.role === 'houseOwner' && jobStatus === 'in-progress') {
    const shoveler = ShovelerInfo[0]?.ShovelerId;
    return (
      <div className="flex flex-col items-center text-center space-y-6 py-6">
        <div className="flex flex-col items-center space-y-4">
          <div className="flex items-center gap-4">
            <img
              src={shoveler?.imageUrl}
              alt={shoveler?.userName}
              className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
            />
          </div>
          <div className="space-y-2">
            <h3 className="font-bold text-xl text-gray-900">
              {shoveler?.userName} is Fulfilling Your Service Request
            </h3>
            <p className="text-gray-600 text-base leading-relaxed max-w-md">
              Your service is currently in progress. You can communicate with the helper or manage your request.
            </p>
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <CancelDialog job={job} />
          <ChatSection job={job} label="Chat with helper" />
          <QueryDialog job={job} />
        </div>
      </div>
    );
  }

  // Open + Authorized/Capture
  if (user.role === 'houseOwner' && jobStatus === 'open' && ['authorize', 'capture'].includes(paymentInfo.status)) {
    return (
      <div className="flex flex-col items-center text-center space-y-6 py-6">
        <div className="flex flex-col items-center space-y-4">
          <Users className="w-12 h-12 text-gray-900" />
          <div className="space-y-2">
            <h3 className="font-bold text-xl text-gray-900">Looking for Neighbourhood Helpers</h3>
            <p className="text-gray-600 text-base leading-relaxed max-w-md">
              Your job is now visible to helpers in your area. You'll be notified when someone accepts it.
            </p>
          </div>
        </div>
        <div className="pt-2 flex items-center gap-2">
          <CancelDialog job={job} />
          <Button variant="secondary" onClick={() => window.location.reload()}>
            <IoIosRefresh className="mr-1" />
            Refresh
          </Button>
        </div>
      </div>
    );
  }

  // Open + Pending
  if (user.role === 'houseOwner' && jobStatus === 'open' && paymentInfo.status === 'pending') {
    return (
      <div className="flex flex-col items-center text-center space-y-6 py-6">
        <div className="flex flex-col items-center space-y-4">
          <AlertCircle className="w-12 h-12 text-gray-900" />
          <div className="space-y-2">
            <h3 className="font-bold text-xl text-gray-900">Payment Authorization Required</h3>
            <p className="text-gray-600 text-base leading-relaxed max-w-md">
              Your payment needs to be authorized for helpers to see this job. Please complete the payment authorization process.
            </p>
            <CancelDialog job={job} />
          </div>
        </div>
      </div>
    );
  }

  // Scheduled + Authorized
  if (user.role === 'houseOwner' && jobStatus === 'scheduled' && paymentInfo.status === 'authorize') {
    const timeRemaining = getTimeRemaining(scheduledDate, scheduledTime);
    return (
      <div className="flex flex-col items-center text-center space-y-6 py-6">
        <div className="flex flex-col items-center space-y-4">
          <Clock className="w-12 h-12 text-gray-900" />
          <div className="space-y-2">
            <h3 className="font-bold text-xl text-gray-900">Job Scheduled</h3>
            <p className="text-gray-600 text-base leading-relaxed max-w-md">
              Your job will be posted in approximately {timeRemaining.days} days, {timeRemaining.hours} hours, and {timeRemaining.minutes} minutes.
            </p>
          </div>
        </div>
        <div className="pt-2">
          <CancelDialog job={job} />
        </div>
      </div>
    );
  }

  // Scheduled + Pending
  if (user.role === 'houseOwner' && jobStatus === 'scheduled' && paymentInfo.status === 'pending') {
    return (
      <div className="flex flex-col items-center text-center space-y-6 py-6">
        <div className="flex flex-col items-center space-y-4">
          <CreditCard className="w-12 h-12 text-gray-900" />
          <div className="space-y-2">
            <h3 className="font-bold text-xl text-gray-900">Payment Authorization Required</h3>
            <p className="text-gray-600 text-base leading-relaxed max-w-md">
              You need to authorize payment with Stripe. Your scheduled job will be deleted within 24 hours if not authorized.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Default case
  return (
    <div className="flex flex-col items-center text-center space-y-6 py-6">
      <div className="flex flex-col items-center space-y-4">
        <Calendar className="w-12 h-12 text-gray-900" />
        <div className="space-y-2">
          <h3 className="font-bold text-xl text-gray-900">Job Status</h3>
          <p className="text-gray-600 text-base leading-relaxed max-w-md">
            Current status: {jobStatus} | Payment: {paymentInfo.status}
          </p>
        </div>
      </div>
    </div>
  );
};

export function JobStatusBanner({ job }) {
  const userInfo = useSelector(state => state.auth.userInfo);
  return (
    <Card className="w-full">
      <CardContent className="pt-6 relative">
        {renderContent(userInfo.user, job)}
      </CardContent>
    </Card>
  );
}